import { useState } from 'react'
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  InputBase,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IMaskInput } from 'react-imask'
import { IconCircleCheckFilled, IconMapPin, IconPlus, IconSearch, IconX } from '@tabler/icons-react'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import type { SavedFacility } from './useRecordsFacilities'
import { normalizeFacilityName } from './authorizationFormData'

type Props = {
  facilities: SavedFacility[]
  loading: boolean
  /** Normalized names of the patient's record locations ("been here"). */
  visitedFacilityNames: Set<string>
  searchQuery: string
  onSearchChange: (q: string) => void
  selectedFacilityId?: string
  onSelect: (facility: SavedFacility) => void
  onAddNew: () => void
  onFaxSaved: (facilityId: string, faxNumber: string) => void
}

// Inline fax entry for a selected facility that has no fax on file (mirrors the rads picker).
function InlineFaxInput({
  facilityId,
  onSaved,
}: {
  facilityId: string
  onSaved: (faxNumber: string) => void
}) {
  const middleware = useMiddlewareClient()
  const [faxValue, setFaxValue] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    const digits = faxValue.replace(/\D/g, '')
    if (digits.length !== 10) {
      notifications.show({ title: 'Invalid fax', message: 'Fax must be 10 digits', color: 'red' })
      return
    }
    setSaving(true)
    try {
      await middleware.post('/order/update-facility-fax', {
        organizationId: facilityId,
        faxNumber: faxValue,
      })
      notifications.show({ title: 'Saved', message: 'Fax number saved', color: 'green' })
      onSaved(faxValue)
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save fax number', color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Group gap="xs" onClick={(e) => e.stopPropagation()}>
      <InputBase
        size="xs"
        placeholder="(000) 000-0000"
        component={IMaskInput}
        mask="(000) 000-0000"
        value={faxValue}
        onAccept={(value) => setFaxValue(value)}
        style={{ width: 140 }}
      />
      <Button size="xs" variant="light" onClick={handleSave} loading={saving}>
        Save Fax
      </Button>
    </Group>
  )
}

export const RecordsFacilityPicker = ({
  facilities,
  loading,
  visitedFacilityNames,
  searchQuery,
  onSearchChange,
  selectedFacilityId,
  onSelect,
  onAddNew,
  onFaxSaved,
}: Props) => {
  return (
    <Paper p="md" withBorder>
      <Group gap="xs" mb="sm">
        <IconMapPin size={20} className="text-[#0097CE]" />
        <Text fw={600} size="lg" className="text-[#0097CE]">
          SELECT FACILITY
        </Text>
      </Group>

      <TextInput
        placeholder="Search facilities by name, city, or zip..."
        leftSection={<IconSearch size={16} />}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.currentTarget.value)}
        mb="md"
        rightSection={
          searchQuery ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Clear search"
              onClick={() => onSearchChange('')}
            >
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
        rightSectionPointerEvents={searchQuery ? 'all' : 'none'}
      />

      {loading && (
        <Group gap="xs" py="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Finding facilities...
          </Text>
        </Group>
      )}

      {!loading && facilities.length === 0 && (
        <Text size="sm" c="dimmed" py="sm">
          No saved facilities{searchQuery ? ' match your search' : ' yet'}. Add one below.
        </Text>
      )}

      {!loading && facilities.length > 0 && (
        <Stack gap="sm" className="max-h-[400px] overflow-y-auto">
          {facilities.map((f) => {
            const isSelected = selectedFacilityId === f.id
            const visited = visitedFacilityNames.has(normalizeFacilityName(f.name))
            return (
              <div
                key={f.id}
                onClick={() => onSelect(f)}
                className={`cursor-pointer rounded-md border p-3 transition-colors ${
                  isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Group gap="xs">
                  <Text fw={600} size="sm">
                    {f.name}
                  </Text>
                  {visited && (
                    <Tooltip label="Patient has been to this facility" withArrow>
                      <IconCircleCheckFilled size={16} className="text-green-600" />
                    </Tooltip>
                  )}
                  {isSelected && (
                    <Badge color="blue" size="xs" variant="light">
                      Selected
                    </Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed" mt={2}>
                  {`${f.street}, ${f.city}, ${f.state} ${f.zip}`}
                </Text>
                <Group gap="md" mt={4}>
                  {f.fax ? (
                    <Text size="xs" c="dimmed">
                      Fax: {f.fax}
                    </Text>
                  ) : isSelected ? (
                    <InlineFaxInput
                      facilityId={f.id}
                      onSaved={(faxNumber) => onFaxSaved(f.id, faxNumber)}
                    />
                  ) : (
                    <Text size="xs" c="red">
                      No fax number on file
                    </Text>
                  )}
                </Group>
              </div>
            )
          })}
        </Stack>
      )}

      <Button
        variant="light"
        leftSection={<IconPlus size={16} />}
        fullWidth
        mt="md"
        onClick={onAddNew}
      >
        Add New Facility
      </Button>
    </Paper>
  )
}
